// SPV Pool v1.0 PR 5 — single source of truth driving the four
// service-provider list + detail surfaces. All four share one shape per
// spec §2.1; this config carries the per-type differences (route, ownerType,
// permission key, statuses, provider-specific Overview fields).

import type { ServiceProviderType } from "@/types/serviceProviders";
import type { WorkTaskRelatedEntityType } from "@/api/workTasks";

export interface ProviderConfig {
  type: ServiceProviderType;
  /** Plural label used for the landing-page internal tab + list heading. */
  pluralLabel: string;
  /** Singular label used for the detail header + "+ New …" button. */
  singularLabel: string;
  /** Route base, e.g. /capital-markets/indenture-trustees. */
  routeBase: string;
  /** Document Access ownerType constant for the cross-cutting tabs. */
  ownerType: string;
  /** WorkTask related-entity type for the Tasks tab. */
  taskType: WorkTaskRelatedEntityType;
  /** Messaging resourceType / Communications-Comm-Log ownerType. */
  messagingType: string;
  /** Permission key gating create / edit. */
  managePermission: string;
  /** Valid status values for the status filter + create form. */
  statuses: readonly string[];
}

export const PROVIDER_CONFIGS: Record<ServiceProviderType, ProviderConfig> = {
  IndentureTrustee: {
    type: "IndentureTrustee",
    pluralLabel: "Indenture Trustees",
    singularLabel: "Indenture Trustee",
    routeBase: "/capital-markets/indenture-trustees",
    ownerType: "indenture_trustee",
    taskType: "IndentureTrustee",
    messagingType: "indenture_trustee",
    managePermission: "IndentureTrustee.Manage",
    statuses: ["Active", "Inactive"],
  },
  TrustCompany: {
    type: "TrustCompany",
    pluralLabel: "Trust Companies",
    singularLabel: "Trust Company",
    routeBase: "/capital-markets/trust-companies",
    ownerType: "trust_company",
    taskType: "TrustCompany",
    messagingType: "trust_company",
    managePermission: "TrustCompany.Manage",
    statuses: ["Active", "Inactive"],
  },
  PlacementAgent: {
    type: "PlacementAgent",
    pluralLabel: "Placement Agents",
    singularLabel: "Placement Agent",
    routeBase: "/capital-markets/placement-agents",
    ownerType: "placement_agent",
    taskType: "PlacementAgent",
    messagingType: "placement_agent",
    managePermission: "PlacementAgent.Manage",
    statuses: ["Active", "Inactive"],
  },
  BackupServicer: {
    type: "BackupServicer",
    pluralLabel: "Backup Servicers",
    singularLabel: "Backup Servicer",
    routeBase: "/capital-markets/backup-servicers",
    ownerType: "backup_servicer",
    taskType: "BackupServicer",
    messagingType: "backup_servicer",
    managePermission: "BackupServicer.Manage",
    statuses: ["Standby", "Active", "Inactive"],
  },
};

export const PROVIDER_ORDER: ServiceProviderType[] = [
  "IndentureTrustee",
  "TrustCompany",
  "PlacementAgent",
  "BackupServicer",
];

/** Resolves a provider config from a route base, used by the detail page. */
export function configForRouteBase(routeBase: string): ProviderConfig | undefined {
  return Object.values(PROVIDER_CONFIGS).find((c) => c.routeBase === routeBase);
}
